/**
 * AuthContext - Manejo de sesión de usuario en cliente.
 *
 * - Persiste el user en estado (no en localStorage, se rehidrata al cargar)
 * - Expone `user`, `isAuthenticated`, `hasPermission(code)`, `isSuperuser`
 * - Carga el perfil al montar si hay tokens en localStorage
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { UserInfo } from '@/types';
import { useProfile } from '@/api/useAuth';
import { tokenStorage } from '@/api/client';

type AuthContextType = {
  user: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSuperuser: boolean;
  hasPermission: (code: string) => boolean;
  setUser: (user: UserInfo | null) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const hasToken = !!tokenStorage.getAccess();
  const { data, isLoading, isError } = useProfile({
    enabled: hasToken && !isInitialized,
  });

  useEffect(() => {
    if (data && !isInitialized) {
      setUser(data);
      setIsInitialized(true);
    } else if (isError && !isInitialized) {
      tokenStorage.clear();
      setUser(null);
      setIsInitialized(true);
    } else if (!hasToken && !isInitialized) {
      setIsInitialized(true);
    }
  }, [data, isError, hasToken, isInitialized]);

  const hasPermission = useCallback(
    (code: string) => {
      if (!user) return false;
      if (user.is_superuser) return true;
      return user.permissions.includes(code);
    },
    [user],
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading: isLoading || !isInitialized,
    isSuperuser: user?.is_superuser ?? false,
    hasPermission,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
}
